import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  ChartBar, Envelope, UsersThree, PaperPlaneTilt, TestTube,
  Broadcast, ClipboardText, GearSix, Power, GoogleLogo, MicrosoftOutlookLogo,
  Database, Target, TreeStructure, Key, Shield, MagnifyingGlass,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import ThemeToggle from "@/components/ThemeToggle";
import { initTheme } from "@/lib/theme";

const NAV = [
  { to: "/", label: "Dashboard", icon: ChartBar, id: "nav-dashboard" },
  { to: "/senders/google", label: "Google", icon: GoogleLogo, id: "nav-senders-google", indent: true, parent: "Senders" },
  { to: "/senders/microsoft", label: "Microsoft", icon: MicrosoftOutlookLogo, id: "nav-senders-microsoft", indent: true, parent: "Senders" },
  { to: "/oauth-senders", label: "Outlook OAuth", icon: Key, id: "nav-oauth-senders", indent: true, parent: "Senders" },
  { to: "/recipients", label: "Recipients", icon: UsersThree, id: "nav-recipients" },
  { to: "/content", label: "Content", icon: Envelope, id: "nav-content" },
  { to: "/test-send", label: "Test Send", icon: TestTube, id: "nav-test-send" },
  { to: "/campaigns", label: "Campaigns", icon: Broadcast, id: "nav-campaigns" },
  { to: "/content-pool", label: "Content Pool", icon: Database, id: "nav-content-pool" },
  { to: "/inbox-placement", label: "Inbox Placement", icon: Target, id: "nav-inbox-placement" },
  { to: "/mailbox-test", label: "Mailbox Test", icon: MagnifyingGlass, id: "nav-mailbox-test" },
  { to: "/seed-mailboxes", label: "Seed Gmail", icon: TreeStructure, id: "nav-seed-mailboxes" },
  { to: "/seed-proxy", label: "Seed Proxy", icon: Shield, id: "nav-seed-proxy" },
  { to: "/oauth-projects", label: "OAuth Projects", icon: Key, id: "nav-oauth-projects" },
  { to: "/microsoft-tenants", label: "MS Tenants", icon: MicrosoftOutlookLogo, id: "nav-microsoft-tenants" },
  { to: "/reports", label: "Reports", icon: ClipboardText, id: "nav-reports" },
  { to: "/settings", label: "Settings", icon: GearSix, id: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [pulse, setPulse] = useState(false);
  const [runningCount, setRunningCount] = useState(0);

  useEffect(() => { initTheme(); }, []);

  useEffect(() => {
    let iv;
    const tick = async () => {
      try {
        const { data } = await api.get("/dashboard");
        setRunningCount(data.active_campaigns || 0);
        setPulse((p) => !p);
      } catch (_e) { /* ignore */ }
    };
    tick();
    iv = setInterval(tick, 4000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      <aside className="w-[240px] border-r border-[#2A2A2A] bg-[#050505] flex flex-col shrink-0 sticky top-0 h-screen">
        <div className="px-5 py-6 border-b border-[#2A2A2A]">
          <div className="font-heading text-2xl font-bold tracking-tighter">
            CO<span className="text-[#E1FF00]">/</span>LD
          </div>
          <div className="micro mt-1">Sending Command Center</div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          <SectionHeader title="Overview" />
          <NavItem item={NAV[0]} />
          <SectionHeader title="Senders" />
          <NavItem item={NAV[1]} />
          <NavItem item={NAV[2]} />
          <NavItem item={NAV[3]} />
          <SectionHeader title="Data" />
          <NavItem item={NAV[4]} />
          <NavItem item={NAV[5]} />
          <SectionHeader title="Operations" />
          <NavItem item={NAV[6]} />
          <NavItem item={NAV[7]} />
          <NavItem item={NAV[8]} />
          <SectionHeader title="Deliverability" />
          <NavItem item={NAV[9]} />
          <NavItem item={NAV[10]} />
          <NavItem item={NAV[11]} />
          <NavItem item={NAV[12]} />
          <NavItem item={NAV[13]} />
          <NavItem item={NAV[14]} />
          <SectionHeader title="System" />
          <NavItem item={NAV[15]} />
          <NavItem item={NAV[16]} />
        </nav>

        <div className="border-t border-[#2A2A2A] p-4 space-y-3">
          <div className="flex items-center gap-2 text-xs">
            <span className={`inline-block w-2 h-2 ${runningCount > 0 ? "bg-[#E1FF00]" : "bg-[#8E8E93]"}`}
                  style={{ boxShadow: runningCount > 0 ? "0 0 10px rgba(225,255,0,0.6)" : "none" }} />
            <span className="font-mono text-[11px] uppercase tracking-wider text-[#A1A1AA]">
              {runningCount > 0 ? `${runningCount} campaign${runningCount > 1 ? "s" : ""} live` : "idle"}
            </span>
          </div>
          <div className="text-xs text-[#71717A] font-mono truncate" data-testid="user-email">
            {user?.email}
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-white transition-colors"
          >
            <Power size={14} weight="bold" />
            SIGN OUT
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden pb-20">
        <div className="topbar flex items-center justify-end gap-3 px-6 py-3 border-b border-[#2A2A2A] bg-[#0A0A0A] sticky top-0 z-30">
          <ThemeToggle />
        </div>
        <Outlet key={location.pathname} />
      </main>
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div className="px-5 pt-5 pb-2 micro">{title}</div>
  );
}

function NavItem({ item }) {
  const Icon = item.icon;
  return (
    <NavLink
      data-testid={item.id}
      to={item.to}
      end={item.to === "/"}
      className={({ isActive }) =>
        `flex items-center gap-3 px-5 py-2 text-sm transition-colors border-l-2 ${
          isActive
            ? "border-[#E1FF00] bg-[#0F0F0F] text-white"
            : "border-transparent text-[#A1A1AA] hover:text-white hover:bg-[#0D0D0D]"
        } ${item.indent ? "pl-8" : ""}`
      }
    >
      <Icon size={16} weight="bold" />
      <span className="font-medium">{item.label}</span>
    </NavLink>
  );
}
